import React, { useEffect, useState } from "react";
import Footer from "../components/Footer";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router";
import MainNav from "../components/navbar/MainNav";
import contactImg from "../assets/payment-successful.png"; // Import the success image
import sadSproutImg from "../assets/contact.png"; // You'll need to add this image
import { processProjectPaymentSuccess, checkProjectContribution } from "../utils/processProjectPayment";
import { CircularProgress } from "@mui/material";

export default function PaymentResponse() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const isSuccess = pathname === "/payment-successful";
  const [projectName, setProjectName] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);
  const [contributionAmount, setContributionAmount] = useState(0);
  
  useEffect(() => {
    const processContribution = async () => {
      const projectId = checkProjectContribution(search);
      
      if (projectId && isSuccess) {
        setProcessingPayment(true);
        try {
          const result = await processProjectPaymentSuccess(projectId, 10); // $10 default contribution
          if (result.success) {
            setProjectName(result.projectName);
            setContributionAmount(result.contributionAmount);
          } else {
            console.error("Failed to process project contribution:", result.error);
          }
        } catch (error) {
          console.error("Error processing project contribution:", error);
        } finally {
          setProcessingPayment(false);
        }
      }
    };

    processContribution();
  }, [search, isSuccess]);

  return (
    <>
      <StyledPaymentResponse>
        <MainNav />
        <div className="main-container">
          <div className="response-container">
            <div className="image-container">
              {isSuccess ? (
                <img
                  src={contactImg}
                  alt="Success"
                  className="response-image"
                />
              ) : (
                <img
                  src={sadSproutImg}
                  alt="Failed"
                  className="response-image"
                />
              )}
            </div>

            <h1 className="main-heading">
              {isSuccess
                ? (projectName ? "Project Contribution Successful!" : "Your Subscription is Successful!")
                : (projectName ? "Project Contribution Failed" : "Subscription Failed")}
            </h1>

            {processingPayment && isSuccess && (
              <div className="processing-indicator">
                <CircularProgress size={24} style={{ marginRight: '12px' }} />
                <span>Processing your contribution...</span>
              </div>
            )}

            <p className="message">
              {isSuccess
                ? (projectName 
                    ? `Thank you for your generous $${contributionAmount} contribution to "${projectName}"! Your support is helping us make a real difference in environmental restoration. Every dollar contributed brings us closer to our goal and helps create a more sustainable future.`
                    : "We sincerely appreciate your payment! Your support means a great deal to us, and we want to express our heartfelt gratitude for your contribution. Thank you for being such a valued part of our community!")
                : (projectName
                    ? "We're sorry, but there was an issue processing your project contribution. Please try again or contact our support team if you continue experiencing problems."
                    : "We're sorry, but there was an issue processing your subscription payment. Please try again or contact our support team if you continue experiencing problems.")}
            </p>

            <div className="action-buttons">
              <button
                className="action-button primary-btn"
                onClick={() => navigate("/")}
              >
                <span className="button-icon">←</span> Back to Home
              </button>
              {projectName && isSuccess && (
                <button
                  className="action-button outline-btn"
                  onClick={() => navigate("/projects")}
                >
                  <span className="button-icon">🌱</span> View Projects
                </button>
              )}
            </div>
          </div>
        </div>
      </StyledPaymentResponse>
    </>
  );
}

const StyledPaymentResponse = styled.section`
  min-height: 80vh;
  padding: 20px 0;
  background: linear-gradient(
    to bottom,
    #b2ebf2 -7.74%,
    #dcedc8 51.74%,
    transparent 50% 100%
  );
  position: relative;
  .main-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 70vh;
  }
  .response-container {
    max-width: 500px;
    width: 90%;
    padding: 3rem 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    background-color: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
    margin-top: 60px;
  }

  .image-container {
    position: relative;
  }

  .response-image {
    width: 90%;
    height: auto;
    object-fit: contain;
    margin-bottom: 0.5rem;
  }

  .size-info {
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #4b9ce2;
    color: white;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
  }

  .main-heading {
    font-size: 2.2rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 1.5rem;
    position: relative;
    z-index: 1;
  }

  .main-heading::before {
    content: "Thank You!";
    position: absolute;
    font-size: 5rem;
    color: rgba(0, 0, 0, 0.03);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    white-space: nowrap;
    z-index: -1;
  }

  .processing-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    color: var(--primary-color);
    font-weight: 500;
  }

  .message {
    font-size: 1.1rem;
    color: #666;
    line-height: 1.6;
    margin-bottom: 2.5rem;
    max-width: 500px;
  }

  .action-buttons {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .action-button {
    color: #333;
    border: none;
    padding: 12px 25px;
    font-size: 1rem;
    font-weight: 500;
    border-radius: 30px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
      /* transform: translateY(-2px); */
    }

    .button-icon {
      font-size: 1.2rem;
    }
  }
  
  .outline-btn {
    background: transparent;
    border: 2px solid var(--primary-color);
    color: var(--primary-color);
    
    &:hover {
      background: var(--primary-color);
      color: white;
    }
  }

  @media (max-width: 768px) {
    padding: 10px;

    .response-container {
      width: 95%;
      padding: 2rem 1rem;
      margin-top: 40px;
    }

    .main-heading {
      font-size: 1.8rem;
    }

    .message {
      font-size: 1rem;
    }

    .response-image {
      width: 200px;
    }

    .main-heading::before {
      font-size: 3rem;
    }
  }

  @media (max-width: 480px) {
    .response-container {
      padding: 1.5rem 0.75rem;
    }

    .main-heading {
      font-size: 1.5rem;
    }

    .message {
      font-size: 0.9rem;
    }

    .response-image {
      width: 150px;
    }

    .main-heading::before {
      font-size: 2.5rem;
    }
  }
`;
